import { SectionStyleField } from "../section-style.fields";

const FooterFields = {
  logo: {
    type: "text",
    label: "Logo URL",
    metadata: {
      source: "context",
      contextKey: "business.logoUrl",
    },
  },
  companyName: {
    type: "text",
    label: "Company Name",
    metadata: {
      source: "context",
      contextKey: "business.name",
    },
  },
  logoDisplay: {
    type: "radio",
    label: "Logo Display",
    options: [
      { label: "Logo only (hide text when logo exists)", value: "logo" },
      { label: "Text only (hide logo)", value: "text" },
      { label: "Both logo and text", value: "both" },
    ],
    metadata: {
      source: "manual",
      description:
        "Controls whether to show the logo image, company name text, or both in the footer. If no logo is uploaded, text will always be shown as fallback.",
    },
  },
  description: {
    type: "textarea",
    label: "Company Description",
    metadata: {
      source: "ai",
      description: "Short company blurb for footer (1-2 sentences)",
      seo: true,
      key: "footer.about",
    },
  },
  linkColumns: {
    type: "array",
    label: "Link Columns",
    metadata: {
      source: "manual",
    },
    getItemSummary: (item: { title: string }) => item.title || "New Column",
    arrayFields: {
      title: { type: "text", label: "Column Title" },
      links: {
        type: "array",
        label: "Links",
        getItemSummary: (item: { label: string }) => item.label || "New Link",
        arrayFields: {
          label: { type: "text", label: "Link Text" },
          linkType: {
            type: "radio",
            label: "Link Type",
            options: [
              { label: "Page", value: "page" },
              { label: "URL", value: "url" },
              { label: "Anchor", value: "anchor" },
            ],
          },
          url: {
            type: "custom", // Render function handles this in config
            label: "Destination",
          },
        },
      },
    },
  },
  showSocial: {
    type: "radio",
    label: "Show Social Links",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  socialLinks: {
    type: "object",
    label: "Social Media Links",
    metadata: {
      source: "context", // Could be partial context? For now manual or manual-context hybrid.
    },
    objectFields: {
      facebook: { type: "text", label: "Facebook URL" },
      twitter: { type: "text", label: "Twitter URL" },
      linkedin: { type: "text", label: "LinkedIn URL" },
      instagram: { type: "text", label: "Instagram URL" },
    },
  },
  showContact: {
    type: "radio",
    label: "Show Contact Info",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  phone: {
    type: "text",
    label: "Phone Number",
    metadata: {
      source: "context",
      contextKey: "business.phone",
    },
  },
  address: {
    type: "textarea",
    label: "Physical Address",
    metadata: {
      source: "context",
      contextKey: "business.address_full", // Will need to make sure this exists or handled
    },
  },
  showBusinessHours: {
    type: "radio",
    label: "Show Business Hours",
    options: [
      { label: "Yes", value: "yes" },
      { label: "No", value: "no" },
    ],
    metadata: {
      source: "manual",
    },
  },
  businessHours: {
    type: "array",
    label: "Business Hours",
    metadata: {
      source: "context", // Complex to map 1:1, usually manual or specific generator logic
    },
    getItemSummary: (item: { day: string }) => item.day || "New Day",
    arrayFields: {
      day: {
        type: "select",
        label: "Day",
        options: [
          { label: "Monday", value: "Monday" },
          { label: "Tuesday", value: "Tuesday" },
          { label: "Wednesday", value: "Wednesday" },
          { label: "Thursday", value: "Thursday" },
          { label: "Friday", value: "Friday" },
          { label: "Saturday", value: "Saturday" },
          { label: "Sunday", value: "Sunday" },
        ],
      },
      hours: {
        type: "text",
        label: "Hours (e.g., 9:00 AM - 5:00 PM)",
      },
      closed: {
        type: "radio",
        label: "Closed?",
        options: [
          { label: "Open", value: false },
          { label: "Closed", value: true },
        ],
      },
    },
  },
  copyrightText: {
    type: "text",
    label: "Copyright Text",
    metadata: {
      source: "manual", // Template handled
      description:
        "Use {year} for the current year so it stays right without republishing. A year typed after the © is kept up to date automatically too.",
    },
  },
  style: {
    type: "select",
    label: "Footer Style",
    options: [
      { label: "Simple", value: "simple" },
      { label: "Multi-Column", value: "multi-column" },
      { label: "Centered", value: "centered" },
    ],
    metadata: {
      source: "manual",
    },
  },
  // Bold closes the page on a dark band, below the bold contact section.
  variant: SectionStyleField,
} as const;

export { FooterFields };
